import AsyncStorage from "@react-native-async-storage/async-storage";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { PersistQueryClientOptions } from "@tanstack/react-query-persist-client";

const asyncStoragePersister = createAsyncStoragePersister({
  storage: AsyncStorage,
  key: "florafolio:query-cache",
});

/**
 * Persist the whole cache — florafolio works offline, so a cold start needs the
 * last-known collections / plants / photos / schedules / journal on hand. The
 * mutation cache is persisted too, so a write made offline survives an app
 * restart and is replayed by `resumePausedMutations()` once back online.
 *
 * Taxonomy queries (`florafolio-taxa*`) are shared public data fetched live;
 * no need to keep them across restarts.
 */
export const persistOptions: Omit<PersistQueryClientOptions, "queryClient"> = {
  persister: asyncStoragePersister,
  // Never age out the offline mirror — a user may not open the app for weeks
  // and still expects their collection on hand offline. The server copy
  // reconciles on the first refetch; freshness is staleTime's job, not this.
  maxAge: Infinity,
  dehydrateOptions: {
    shouldDehydrateQuery: (query) =>
      typeof query.queryKey[0] === "string" &&
      !query.queryKey[0].startsWith("florafolio-taxa") &&
      query.queryKey[0] !== "florafolio-taxon-lineage",
    shouldDehydrateMutation: () => true,
  },
};
